import type { ButtonProps } from '@openx/components-core/lib/Button/Button';
import type { UseConfirmationDialogParams } from '@openx/components-core/lib/Dialog/ConfirmationDialog/types';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { dealStatus, type OaDealStatus } from '@openx/types/deal';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DealRowActionType } from '../../../constants';
import { DealsOperationDialogContent } from '../../components/DealsOperationDialogContent';
import { canArchiveDeal } from '../canArchiveDeal';
import { useDealContext } from '../context/DealContext';
import type { DealActionResult, DealRowActionPayload } from '../context/DealHookTypes';

import { useDealRowActionTranslations } from './useDealRowActionTranslations';

interface GetActionModalParams<T extends DealRowActionPayload = DealRowActionPayload> {
  content?: ReactNode;
  deals: T[];
  isBulkMode?: boolean;
  onConfirmed(filteredDeals: DealRowActionPayload[]): Promise<void>;
  action: DealRowActionType;
  title: string;
  confirmationDialog: {
    open: (params: Partial<UseConfirmationDialogParams>) => void;
    close: () => void;
    setLoading: (loading: boolean) => void;
  };
  confirmationLabel: string;
  confirmationButtonColor?: ButtonProps['color'];
  onCancel?: () => void;
  isOa?: boolean;
  showRBOAlert?: boolean;
}

const actionModal = ({
  content,
  deals,
  isBulkMode = false,
  onConfirmed,
  action,
  title,
  confirmationDialog,
  confirmationLabel,
  confirmationButtonColor,
  onCancel,
  isOa,
  showRBOAlert,
}: GetActionModalParams<DealRowActionPayload>) => {
  const { open, close } = confirmationDialog;
  let filteredDeals = deals;

  if (action === DealRowActionType.PAUSE) {
    filteredDeals = deals.filter(deal => deal.status === dealStatus.Active && !deal.is_direct_deal);
  } else if (action === DealRowActionType.RESUME) {
    filteredDeals = deals.filter(deal => deal.status === dealStatus.Paused && !deal.is_direct_deal);
  } else if (action === DealRowActionType.DELETE && isOa) {
    filteredDeals = deals.filter(
      deal => canArchiveDeal(deal.status as Partial<OaDealStatus> as OaDealStatus) && !deal.is_direct_deal
    );
  }

  open({
    confirmationButtonColor,
    confirmationLabel,
    content: (
      <DealsOperationDialogContent
        content={content}
        deals={filteredDeals}
        action={action}
        isBulkMode={isBulkMode}
        allDeals={deals.length}
        isOa={isOa}
        showRBOAlert={showRBOAlert}
      />
    ),
    customContent: true,
    hideConfirm: filteredDeals.length === 0,
    onCancel: () => {
      onCancel?.();
    },
    onConfirmed: async () => {
      try {
        await onConfirmed(filteredDeals);
      } finally {
        close();
      }
    },
    showExtraBtn: false,
    title: title,
  });
};

export const useDealsOperations = () => {
  const { t } = useTranslation();
  const confirmationDialog = useConfirmationDialog();
  const { useDealOperations, isOa } = useDealContext();

  const {
    cloneDeals,
    deleteDeals,
    resumeDeals,
    pauseDeals,
    onConfirmed: onConfirmedCallback,
    downloadDeals,
  } = useDealOperations();

  const [loadingAction, setLoadingAction] = useState<DealRowActionType | null>(null);

  const setLoading = useCallback(
    (action: DealRowActionType | null) => {
      setLoadingAction(action);
      confirmationDialog.setLoading(!!action);
    },
    [confirmationDialog]
  );

  const actions = useMemo(
    () => ({
      [DealRowActionType.DELETE]: deleteDeals,
      [DealRowActionType.CLONE]: cloneDeals,
      [DealRowActionType.RESUME]: resumeDeals,
      [DealRowActionType.PAUSE]: pauseDeals,
      [DealRowActionType.DOWNLOAD]: downloadDeals,
    }),
    [cloneDeals, deleteDeals, pauseDeals, resumeDeals, downloadDeals]
  );

  const actionTranslations = useDealRowActionTranslations();

  const callDealAction = useCallback(
    <T, V extends DealRowActionPayload = DealRowActionPayload>({
      action,
      content,
      deals,
      isBulkMode = false,
      skipModal = false,
      preProcessDeals = (deals): V[] => deals as unknown as V[],
      onFinish,
      onCancel,
      showRBOAlert,
    }: {
      action: DealRowActionType;
      content?: ReactNode;
      deals: DealRowActionPayload[];
      isBulkMode?: boolean;
      skipModal?: boolean;
      onFinish?: (actionResult?: DealActionResult<T>) => void;
      onCancel?: () => void;
      preProcessDeals?: (deals: DealRowActionPayload[]) => V[];
      showRBOAlert?: boolean;
    }) => {
      const onConfirmed = (deals: DealRowActionPayload[]) => {
        const processedDeals = preProcessDeals(deals);

        return onConfirmedCallback<T, V>({
          action,
          actionCallback: actions[action],
          deals: processedDeals,
          isBulkMode,
          onFinish,
          setLoading,
        });
      };

      if (skipModal) {
        return onConfirmed(deals);
      }

      const confirmationButtonColor = (
        [DealRowActionType.CLONE, DealRowActionType.RESUME] as DealRowActionType[]
      ).includes(action)
        ? 'primary'
        : 'error';
      const dealSuffix = isBulkMode ? t('Deals') : t('Deal');

      actionModal({
        action,
        confirmationButtonColor,
        confirmationDialog,
        confirmationLabel: actionTranslations[action].toLowerCase(),
        content,
        deals,
        isBulkMode,
        isOa,
        onCancel,
        onConfirmed,
        title: `${actionTranslations[action]} ${dealSuffix}`,
        showRBOAlert,
      });
    },
    [actionTranslations, actions, confirmationDialog, onConfirmedCallback, setLoading, t, isOa]
  );

  return { callDealAction, loadingAction };
};
